<!--
 * @Author: LiZhiWei
 * @Date: 2026-08-21 15:59:00
 * @LastEditors: LiZhiWei
 * @LastEditTime: 2026-09-01 15:12:28
 * @Description: 
-->
<template>
  <div class="mall-page">
    <inner-hero
      :banners="banners"
      :background-image="FALLBACK_MALL_IMAGE"
      title="兴科商城"
      :show-breadcrumb="false"
    />
    <product-browser />
    <contact-action />
  </div>
</template>

<script setup lang="ts">
  import type { ApiData, BannerItem, ProductCategory } from '~/types/site-api'

  import {
    createBreadcrumbJsonLd,
    createSiteUrl,
    getPageSeo,
    getSiteUrl,
    siteSeo,
  } from '~/utils/seo'

  definePageMeta({ layout: 'default' })

  const seo = getPageSeo('mall')
  const siteUrl = getSiteUrl()

  const route = useRoute()
  const { getBanners, getProductCategories } = useSiteApi()

  /** banner 接口无数据或请求失败时的兜底背景图。 */
  const FALLBACK_MALL_IMAGE = '/images/article/article-hero.webp'

  /** 复用 product-browser 的分类请求 key，避免重复拉取。 */
  const { data: categoryData } = await useAsyncData<ApiData<ProductCategory[]>>(
    'site-product-categories',
    getProductCategories
  )
  const productCategories = computed(() => categoryData.value?.data ?? [])

  const selectedCategoryCode = computed(() => {
    const value = route.query.category
    return Array.isArray(value)
      ? (value.find((item): item is string => item !== null) ?? '')
      : (value ?? '')
  })
  const bannerPosition = computed(() => {
    const code = selectedCategoryCode.value
    const selectedCategory = productCategories.value.find(
      (category) => category.code === code || category.name === code
    )

    return selectedCategory?.id ?? productCategories.value[0]?.id ?? ''
  })

  const { data: bannerData } = await useAsyncData(
    'mall-section-banner',
    async (_nuxtApp, { signal }) => {
      const id = String(bannerPosition.value)
      if (!id) return { data: [] as BannerItem[] }
      try {
        return await getBanners({ position: id }, signal)
      } catch (error) {
        if (signal.aborted) throw error
        return { data: [] as BannerItem[] }
      }
    },
    { watch: [bannerPosition], default: () => ({ data: [] as BannerItem[] }) }
  )

  const banners = computed(() => bannerData.value?.data ?? [])

  useSeoMeta({
    title: seo.title,
    description: seo.description,
    ogTitle: seo.title,
    ogDescription: seo.description,
    ogType: 'website',
    ogImage: siteSeo.ogImage,
    twitterTitle: seo.title,
    twitterDescription: seo.description,
    twitterImage: siteSeo.ogImage,
  })

  useHead({
    meta: [{ name: 'keywords', content: seo.keywords?.join(', ') }],
    link: [{ rel: 'canonical', href: createSiteUrl('/mall', siteUrl) }],
  })
  useJsonLd(
    createBreadcrumbJsonLd(
      [
        { name: '首页', path: '/' },
        { name: '兴科商城', path: '/mall' },
      ],
      siteUrl
    )
  )
</script>

<style scoped lang="scss">
  @media (min-width: 768px) {
    :deep(.inner-hero) {
      min-height: 220px;
    }
  }

  @media (max-width: 767px) {
    :deep(.inner-hero) {
      min-height: 180px;
    }
  }

  :deep(.inner-hero h1) {
    font-size: clamp(28px, 3vw, 40px);
  }

  /* 移除商城页 hero 的渐变遮罩 */
  :deep(.inner-hero)::after {
    content: none;
  }

  /* 商城 hero 收窄至 1280px，与列表区对齐 */
  :deep(.inner-hero-content) {
    @media (min-width: 768px) {
      padding-inline: 320px;
    }

    @media (min-width: 1440px) {
      padding-inline: calc(50vw - 640px);
    }
  }
</style>
